import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';

/**
 * ETD (Evo Test Drive) Context
 * 
 * Handles communication between the demo app and the Evo Test Drive parent iframe.
 * The demo app runs as a child iframe within ETD, which provides product data
 * including customData containing mock data for the demo.
 * 
 * Communication Protocol:
 * - Child sends: ETD_DEMO_READY when the demo is ready to receive data
 * - Parent sends: ETD_PRODUCT_DATA with the product configuration and customData
 */

export interface ETDProductData {
  id: string;
  slug: string;
  name: string;
  summary: string;
  categoryId: string;
  render: {
    mode: 'iframe' | 'embedded';
    fullWidth: boolean;
  };
  routes: Array<{
    path: string;
    label: string;
    iframeUrl?: string;
  }>;
  tags: string[];
  thumbnail?: string;
  logo?: string;
  isUpcoming: boolean;
  customData?: Record<string, unknown>;
}

interface ETDContextValue {
  isETDEnvironment: boolean;
  isReady: boolean;
  productData: ETDProductData | null;
  customData: Record<string, unknown> | null;
  sendMessageToParent: (type: string, payload?: unknown) => void;
}

const ETDContext = createContext<ETDContextValue | undefined>(undefined);

const READY_TIMEOUT_MS = 2000;

interface ETDProviderProps {
  children: ReactNode;
  fallbackProductData?: ETDProductData | Record<string, unknown>;
}

export function ETDProvider({ children, fallbackProductData }: ETDProviderProps) {
  const [isETDEnvironment, setIsETDEnvironment] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [productData, setProductData] = useState<ETDProductData | null>(null);

  const sendMessageToParent = useCallback((type: string, payload?: unknown) => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type, payload }, '*');
    }
  }, []);

  useEffect(() => {
    const isInIframe = window.parent !== window;
    setIsETDEnvironment(isInIframe);

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'ETD_PRODUCT_DATA') {
        const data = event.data.payload as ETDProductData;
        setProductData(data);
        setIsReady(true);
      }
    };

    window.addEventListener('message', handleMessage);

    if (isInIframe) {
      sendMessageToParent('ETD_DEMO_READY');
    }

    const timeoutId = setTimeout(() => {
      if (!isReady) {
        if (fallbackProductData) {
          setProductData(fallbackProductData as ETDProductData);
        }
        setIsReady(true);
      }
    }, READY_TIMEOUT_MS);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeoutId);
    };
  }, [sendMessageToParent, fallbackProductData, isReady]);

  const value: ETDContextValue = {
    isETDEnvironment,
    isReady,
    productData,
    customData: productData?.customData ?? null,
    sendMessageToParent,
  };

  return (
    <ETDContext.Provider value={value}>
      {children}
    </ETDContext.Provider>
  );
}

export function useETD(): ETDContextValue {
  const context = useContext(ETDContext);
  if (context === undefined) {
    throw new Error('useETD must be used within an ETDProvider');
  }
  return context;
}

export function useETDCustomData<T = Record<string, unknown>>(): T | null {
  const { customData } = useETD();
  return customData as T | null;
}
